import { addMonths } from "date-fns";
import type {
  ForecastPoint,
  InvestmentProperty,
  Portfolio,
  PortfolioForecast,
  PropertyForecast,
  Scenario
} from "./models";
import { annualizedIrr, clamp01, sum } from "./metrics";
import { annualCostForExpenseCategories } from "./expenseMath";
import { applyScenarioToProperty } from "../scenarios/scenarioEngine";

interface AmortizationMonth {
  monthIndex: number;
  interestPayment: number;
  feePayment: number;
  principalPayment: number;
  totalPayment: number;
  remainingBalance: number;
}

const MONTHS_IN_YEAR = 12;
const DEFAULT_SELLING_COST_PERCENT = 2.5;

export const monthlyRate = (annualRatePercent: number): number =>
  annualRatePercent / 100 / MONTHS_IN_YEAR;

export const annuityPayment = (
  principal: number,
  annualRatePercent: number,
  remainingMonths: number
): number => {
  if (remainingMonths <= 0) return 0;
  const r = monthlyRate(annualRatePercent);
  if (r === 0) return principal / remainingMonths;
  return (principal * r) / (1 - Math.pow(1 + r, -remainingMonths));
};

export const amortizationSchedule = (
  property: InvestmentProperty,
  forecastYears: number,
  extraRepaymentMonthly = 0
): AmortizationMonth[] => {
  const totalMonths = forecastYears * MONTHS_IN_YEAR;
  const ioMonths =
    property.loan.type === "IO" ? property.loan.ioYears * MONTHS_IN_YEAR : 0;
  const schedule: AmortizationMonth[] = [];

  let balance = Math.max(property.loan.principal, 0);

  for (let monthIndex = 0; monthIndex < totalMonths; monthIndex += 1) {
    const effectiveBalance = Math.max(balance - property.loan.offsetBalance, 0);
    const interest = effectiveBalance * monthlyRate(property.loan.interestRate);
    const monthlyFees = property.loan.annualFees / MONTHS_IN_YEAR;

    let principalPayment = 0;
    let basePayment = 0;

    if (monthIndex < ioMonths) {
      basePayment = interest + monthlyFees;
    } else {
      const remainingMonths = Math.max(
        property.loan.termYears * MONTHS_IN_YEAR - monthIndex,
        1
      );
      basePayment =
        annuityPayment(balance, property.loan.interestRate, remainingMonths) +
        monthlyFees;
      principalPayment = Math.max(basePayment - interest - monthlyFees, 0);
    }

    principalPayment += extraRepaymentMonthly;
    principalPayment = Math.min(principalPayment, balance);

    const totalPayment = Math.min(
      basePayment + extraRepaymentMonthly,
      balance + interest + monthlyFees
    );
    balance = Math.max(balance - principalPayment, 0);

    const feePayment = Math.max(totalPayment - interest - principalPayment, 0);

    schedule.push({
      monthIndex,
      interestPayment: interest,
      feePayment,
      principalPayment,
      totalPayment,
      remainingBalance: balance
    });

    if (balance <= 0) {
      for (let i = monthIndex + 1; i < totalMonths; i += 1) {
        schedule.push({
          monthIndex: i,
          interestPayment: 0,
          feePayment: 0,
          principalPayment: 0,
          totalPayment: 0,
          remainingBalance: 0
        });
      }
      break;
    }
  }

  return schedule;
};

export const forecastProperty = (
  propertyInput: InvestmentProperty,
  years: number,
  scenario?: Scenario
): PropertyForecast => {
  const property = scenario
    ? applyScenarioToProperty(propertyInput, scenario)
    : propertyInput;
  const schedule = amortizationSchedule(
    property,
    years,
    scenario?.kind === "EXTRA_REPAYMENT"
      ? (scenario.extraRepaymentMonthly ?? 0)
      : 0
  );
  const yearlyPoints: ForecastPoint[] = [];

  const initialCashOut =
    property.purchase.deposit +
    property.purchase.stampDuty +
    property.purchase.legalFees +
    property.purchase.inspections +
    property.purchase.lmi +
    property.purchase.renovationCapex;

  const cashFlows: number[] = [-initialCashOut];

  let propertyValue = property.purchase.price;
  let annualRent = property.income.weeklyRent * 52;
  let annualFixedExpense =
    property.expenses.councilRates +
    property.expenses.insurance +
    property.expenses.maintenance +
    property.expenses.strata +
    property.expenses.water +
    property.expenses.utilities +
    property.expenses.agentFeesAnnual;

  const startDate = new Date(property.purchase.purchaseDate);
  const sellYear =
    scenario?.kind === "SELL_IN_YEAR" && typeof scenario.sellYear === "number"
      ? scenario.sellYear
      : null;
  const saleOccursWithinHorizon = sellYear !== null && sellYear <= years;

  for (let year = 1; year <= years; year += 1) {
    const growthRate = property.growth.capitalGrowthRate / 100;
    propertyValue *= 1 + growthRate;

    if (year > 1) {
      annualRent *= 1 + property.growth.rentGrowthRate / 100;
      annualFixedExpense *= 1 + property.growth.expenseInflationRate / 100;
    }

    const vacancyImpact =
      annualRent * clamp01(property.expenses.vacancyRate / 100);
    const managedRent = annualRent - vacancyImpact;

    const managementCost =
      managedRent * (property.expenses.managementPercent / 100);
    const categoryExpenseTotal = annualCostForExpenseCategories(
      property.expenses.categories,
      year,
      property.expenses.expenseInflationRate
    );

    const annualIncome = managedRent + property.income.otherAnnualIncome;
    const annualExpenses =
      annualFixedExpense + managementCost + categoryExpenseTotal;

    const monthStart = (year - 1) * MONTHS_IN_YEAR;
    const monthEnd = monthStart + MONTHS_IN_YEAR;
    const annualLoanPayments = sum(
      schedule.slice(monthStart, monthEnd).map((m) => m.totalPayment)
    );
    const annualInterestAndFees = sum(
      schedule
        .slice(monthStart, monthEnd)
        .map((m) => m.interestPayment + m.feePayment)
    );
    const loanBalance =
      schedule[Math.min(monthEnd - 1, schedule.length - 1)]?.remainingBalance ??
      0;

    let netCashflowPreTax = annualIncome - annualExpenses - annualLoanPayments;

    if (
      sellYear !== null &&
      year === sellYear
    ) {
      const sellingCosts = propertyValue * (DEFAULT_SELLING_COST_PERCENT / 100);
      const saleProceeds = propertyValue - sellingCosts - loanBalance;
      netCashflowPreTax += saleProceeds;
    }

    const taxableIncome =
      annualIncome -
      annualExpenses -
      annualInterestAndFees -
      property.tax.annualDepreciation;
    const marginalRate = property.tax.marginalTaxRate / 100;
    const taxAdjustment =
      taxableIncome < 0
        ? property.tax.negativeGearingEnabled
          ? Math.abs(taxableIncome) * marginalRate
          : 0
        : -taxableIncome * marginalRate;
    const netCashflowAfterTax = netCashflowPreTax + taxAdjustment;

    const isTerminalYear =
      year === years && !saleOccursWithinHorizon;

    if (isTerminalYear) {
      // Include terminal equity so IRR reflects the asset value, not just yearly cashflow.
      // Note: excludes CGT/transactional complexity for now; this is an approximation.
      const sellingCosts = propertyValue * (DEFAULT_SELLING_COST_PERCENT / 100);
      const terminalProceeds = propertyValue - sellingCosts - loanBalance;
      cashFlows.push(netCashflowAfterTax + terminalProceeds);
    } else {
      cashFlows.push(netCashflowAfterTax);
    }

    const grossYield = annualIncome / propertyValue;
    const netYield = (annualIncome - annualExpenses) / propertyValue;
    const equity = propertyValue - loanBalance;
    const lvr = propertyValue > 0 ? loanBalance / propertyValue : 0;

    yearlyPoints.push({
      year,
      value: propertyValue,
      loanBalance,
      equity,
      annualIncome,
      annualExpenses,
      annualLoanPayments,
      netCashflowPreTax,
      taxBenefit: taxAdjustment,
      netCashflowAfterTax,
      grossYield,
      netYield,
      lvr
    });

    if (
      sellYear !== null &&
      year >= sellYear
    ) {
      break;
    }

    addMonths(startDate, MONTHS_IN_YEAR * year);
  }

  const irrApprox = annualizedIrr(cashFlows);
  const totalNetCashflow = sum(
    yearlyPoints.map((point) => point.netCashflowAfterTax)
  );
  const cashOnCashReturn =
    initialCashOut > 0 ? totalNetCashflow / initialCashOut : 0;

  return {
    propertyId: property.id,
    points: yearlyPoints,
    irrApprox,
    cashOnCashReturn,
    totalNetCashflow
  };
};

const aggregatePortfolioPoints = (
  forecasts: PropertyForecast[],
  years: number
): ForecastPoint[] => {
  const points: ForecastPoint[] = [];
  for (let year = 1; year <= years; year += 1) {
    const yearPoints = forecasts
      .map((forecast) => forecast.points.find((point) => point.year === year))
      .filter((point): point is ForecastPoint => Boolean(point));

    if (yearPoints.length === 0) continue;

    const totalValue = sum(yearPoints.map((point) => point.value));
    const totalLoan = sum(yearPoints.map((point) => point.loanBalance));
    const totalIncome = sum(yearPoints.map((point) => point.annualIncome));
    const totalExpenses = sum(yearPoints.map((point) => point.annualExpenses));
    const totalLoanPayments = sum(
      yearPoints.map((point) => point.annualLoanPayments)
    );
    const totalPreTax = sum(yearPoints.map((point) => point.netCashflowPreTax));
    const totalTaxBenefit = sum(yearPoints.map((point) => point.taxBenefit));
    const totalAfterTax = sum(
      yearPoints.map((point) => point.netCashflowAfterTax)
    );

    points.push({
      year,
      value: totalValue,
      loanBalance: totalLoan,
      equity: totalValue - totalLoan,
      annualIncome: totalIncome,
      annualExpenses: totalExpenses,
      annualLoanPayments: totalLoanPayments,
      netCashflowPreTax: totalPreTax,
      taxBenefit: totalTaxBenefit,
      netCashflowAfterTax: totalAfterTax,
      grossYield: totalValue > 0 ? totalIncome / totalValue : 0,
      netYield: totalValue > 0 ? (totalIncome - totalExpenses) / totalValue : 0,
      lvr: totalValue > 0 ? totalLoan / totalValue : 0
    });
  }

  return points;
};

export const forecastPortfolio = (
  portfolio: Portfolio,
  scenario?: Scenario
): PortfolioForecast => {
  const years = portfolio.assumptions.forecastYears;
  const propertyForecasts = portfolio.properties.map((property) =>
    forecastProperty(property, years, scenario)
  );

  return {
    propertyForecasts,
    portfolioPoints: aggregatePortfolioPoints(propertyForecasts, years)
  };
};
